/**
 * Scene-local state shared by the R3F layers and the imperative handle: the
 * active model root and its resolved bindings, the asset bounds, animation
 * locks that decide between on-demand and continuous rendering, the sun, and
 * the camera flights in progress. One scene is mounted at a time, so this is a
 * module singleton reset by TwinStage on mount.
 */
import * as THREE from 'three';
import { createStore } from 'zustand/vanilla';
import type { CameraPose } from '../types';

export interface BoundNode {
  id: string;
  /** Named nodes or anchors the binding resolved to. */
  objects: THREE.Object3D[];
  /** Every mesh under those objects (a deeper binding may own some of them). */
  meshes: THREE.Mesh[];
  /** Rest centre in world space (the model is normalised at the origin). */
  centre: THREE.Vector3;
  radius: number;
  /** Rest centre expressed in objects[0]'s local frame, so it follows explode offsets. */
  centreLocal: THREE.Vector3;
  /** Direction (model frame, when the bindings resolved) and length of the node's own explode offset (explode.ts). */
  explodeDir: THREE.Vector3;
  explodeDist: number;
  /** 0 for a group enclosing the whole model, 1 for the top-level parts, one more per moving ancestor. */
  explodeLevel: number;
  /** A member of a coaxial stack (explode.ts): the dish's layers; the exploded view frames the largest stack obliquely. */
  explodeStacked: boolean;
  /** The own offset at full explode per object, in that object's parent frame (added to its rest position). */
  explodeLocal: Map<THREE.Object3D, THREE.Vector3>;
  nested: boolean;
  parentId?: string;
  anchorOnly: boolean;
  /** Camera pose authored in the spec, if any. */
  pose?: CameraPose;
  restPositions: Map<THREE.Object3D, THREE.Vector3>;
}

export interface Flight {
  seq: number;
  poseId: string | null;
  from: CameraPose;
  to: CameraPose;
  startMs: number;
  durationMs: number;
}

export interface DebugCounts {
  meshes: number;
  labels: number;
  packets: number;
}

export interface SceneState {
  /** Increments whenever the active root or its bindings change. */
  epoch: number;
  root: THREE.Object3D | null;
  view: 'main' | string;
  bindings: Map<string, BoundNode>;
  meshOwner: Map<THREE.Mesh, string>;
  unbound: string[];
  /** Ids bound in the main model (what the handle reports). */
  mainBound: string[];
  mainCentre: THREE.Vector3;
  mainRadius: number;
  assetCentre: THREE.Vector3;
  assetRadius: number;
  /** Size of the active model's bounding box (centred on assetCentre), for the main framing. */
  assetSize: THREE.Vector3;
  isSpace: boolean;
  sun: { azimuthDeg: number; elevationDeg: number };
  locks: Set<string>;
  flight: Flight | null;
  /** Explode amount currently applied to the nodes (eased towards the store value). */
  explodeApplied: number;
  packetCount: number;
  selectedObjects: THREE.Object3D[];
  aspect: number;
  /** The R3F scene, for the debug counters. */
  scene: THREE.Scene | null;

  setScene: (scene: THREE.Scene | null) => void;
  setRoot: (
    root: THREE.Object3D | null,
    view: 'main' | string,
    resolved: { bindings: Map<string, BoundNode>; meshOwner: Map<THREE.Mesh, string>; unbound: string[]; anchors?: THREE.Object3D[] },
    bounds: { centre: THREE.Vector3; radius: number; size: THREE.Vector3 },
    isSpace: boolean,
  ) => void;
  setSun: (azimuthDeg: number, elevationDeg: number) => void;
  lock: (key: string) => void;
  unlock: (key: string) => void;
  startFlight: (flight: Flight) => void;
  endFlight: () => void;
  setExplodeApplied: (v: number) => void;
  setPacketCount: (n: number) => void;
  setSelectedObjects: (objects: THREE.Object3D[]) => void;
  setAspect: (aspect: number) => void;
  reset: () => void;
}

const initial = () => ({
  epoch: 0,
  root: null,
  view: 'main' as const,
  bindings: new Map<string, BoundNode>(),
  meshOwner: new Map<THREE.Mesh, string>(),
  unbound: [] as string[],
  mainBound: [] as string[],
  mainCentre: new THREE.Vector3(),
  mainRadius: 1,
  assetCentre: new THREE.Vector3(),
  assetRadius: 1,
  assetSize: new THREE.Vector3(1, 1, 1),
  isSpace: false,
  sun: { azimuthDeg: 135, elevationDeg: 48 },
  locks: new Set<string>(),
  flight: null as Flight | null,
  explodeApplied: 0,
  packetCount: 0,
  selectedObjects: [] as THREE.Object3D[],
  aspect: 16 / 9,
  scene: null as THREE.Scene | null,
});

export const sceneStore = createStore<SceneState>()((set, get) => ({
  ...initial(),
  setScene: (scene) => set({ scene }),
  setRoot: (root, view, resolved, bounds, isSpace) =>
    set((s) => ({
      epoch: s.epoch + 1,
      root,
      view,
      bindings: resolved.bindings,
      meshOwner: resolved.meshOwner,
      unbound: resolved.unbound,
      mainBound: view === 'main' ? [...resolved.bindings.keys()].filter((id) => !resolved.unbound.includes(id)) : s.mainBound,
      mainCentre: view === 'main' ? bounds.centre.clone() : s.mainCentre,
      mainRadius: view === 'main' ? bounds.radius : s.mainRadius,
      assetCentre: bounds.centre.clone(),
      assetRadius: bounds.radius,
      assetSize: bounds.size.clone(),
      isSpace,
    })),
  setSun: (azimuthDeg, elevationDeg) => set({ sun: { azimuthDeg, elevationDeg } }),
  lock: (key) => {
    if (get().locks.has(key)) return;
    const locks = new Set(get().locks);
    locks.add(key);
    set({ locks });
  },
  unlock: (key) => {
    if (!get().locks.has(key)) return;
    const locks = new Set(get().locks);
    locks.delete(key);
    set({ locks });
  },
  startFlight: (flight) => set({ flight }),
  endFlight: () => set({ flight: null }),
  setExplodeApplied: (explodeApplied) => set({ explodeApplied }),
  setPacketCount: (packetCount) => set({ packetCount }),
  setSelectedObjects: (selectedObjects) => set({ selectedObjects }),
  setAspect: (aspect) => set({ aspect }),
  reset: () => set({ ...initial(), scene: get().scene }),
}));

/** Debug counters for the harness (meshes rendered, labels shown, packets alive). */
export function debugCounts(): DebugCounts {
  let meshes = 0;
  sceneStore.getState().scene?.traverse((o) => {
    if ((o as THREE.Mesh).isMesh && o.visible) meshes++;
  });
  const labels =
    typeof document === 'undefined'
      ? 0
      : [...document.querySelectorAll<HTMLElement>('[data-twin-label]')].filter((el) => el.style.visibility === 'visible').length;
  return { meshes, labels, packets: sceneStore.getState().packetCount };
}

/** Resolvers for flights started through the handle, keyed by camera request seq. */
const flightResolvers = new Map<number, () => void>();

export function awaitFlight(seq: number): Promise<void> {
  return new Promise<void>((resolve) => {
    flightResolvers.set(seq, resolve);
  });
}

export function resolveFlight(seq: number): void {
  const r = flightResolvers.get(seq);
  if (r) {
    flightResolvers.delete(seq);
    r();
  }
}

export function resolveAllFlights(): void {
  for (const r of flightResolvers.values()) r();
  flightResolvers.clear();
}
